// Notifications — supervisor inbox of clinic-access requests to approve or deny.
// A named person at a clinic mailbox has requested access; approving lets them
// set up a PIN and sign in. Gated to manageClinicAccess (Supervisor tier and up).

import { useEffect, useState, useCallback } from 'react';
import { NotificationIcon } from '@phosphor-icons/react/dist/csr/Notification';
import { getClinicRequests, decideClinicRequest } from '../api/client.js';
import { useClinicApprovals } from '../contexts/ClinicApprovalsContext.jsx';

export default function ClinicApprovals() {
  const { setCount } = useClinicApprovals();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [denying, setDenying] = useState(null);   // key of the request being denied (confirm step)
  const [reason, setReason] = useState('');

  // The open page is the source of truth for the badge: whatever the list shows
  // is exactly what the badge counts (kills the count-vs-list drift from a stale
  // polled count). `initial` only toggles the first-load spinner.
  const load = useCallback(async (initial = false) => {
    if (initial) setLoading(true);
    setError(null);
    try { const { requests } = await getClinicRequests(); const list = requests || []; setRequests(list); setCount(list.length); }
    catch (e) { setError(e.message || 'Could not load requests.'); }
    finally { if (initial) setLoading(false); }
  }, [setCount]);

  // Load on mount, then poll so a request that arrives while the page is open
  // shows up (and the badge stays in step).
  useEffect(() => {
    load(true);
    const t = setInterval(() => load(false), 30_000);
    return () => clearInterval(t);
  }, [load]);

  async function decide(req, decision, reasonText) {
    const key = `${req.kind}:${req.id}`;
    setBusyId(key); setError(null);
    try {
      await decideClinicRequest(req.id, decision, req.kind, reasonText);
      setRequests(rs => { const next = rs.filter(r => `${r.kind}:${r.id}` !== key); setCount(next.length); return next; });
      setDenying(null); setReason('');
    } catch (e) {
      setError(e.message || 'Could not action that request.');
      load(false);   // re-sync (it may have been actioned elsewhere)
    } finally { setBusyId(null); }
  }

  return (
    <div className="page" style={{ maxWidth: 760, margin: '0 auto' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <NotificationIcon size={22} weight="regular" color="#6366F1" />
        <h1 style={{ margin: 0, fontSize: 22 }}>Notifications</h1>
      </header>
      <p style={{ color: 'var(--text-2)', fontSize: 13, marginTop: 0 }}>
        Clinic staff waiting for you to confirm who they are. Approving lets them set up a PIN and sign in.
      </p>

      {error && <p style={{ color: 'var(--danger, #DC2626)', fontSize: 13 }}>{error}</p>}

      {loading ? (
        <p style={{ color: 'var(--text-3)' }}>Loading…</p>
      ) : requests.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-3)' }}>
          <NotificationIcon size={30} weight="regular" color="var(--text-3)" />
          <p style={{ marginTop: 8, fontSize: 14 }}>Nothing to action. You're all caught up.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
          {requests.map(r => {
            const key = `${r.kind}:${r.id}`;
            const isReset = r.kind === 'pin_reset';
            return (
            <div key={key} style={{
              display: 'flex', flexDirection: 'column', gap: 12, padding: '14px 16px',
              background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <span style={{
                  fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
                  padding: '3px 8px', borderRadius: 6, flexShrink: 0,
                  background: isReset ? '#FEF3C7' : '#E0E7FF', color: isReset ? '#92400E' : '#3730A3',
                }}>{isReset ? 'PIN reset' : 'New name'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{r.displayName}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 2, overflowWrap: 'anywhere' }}>
                    {r.jobTitle ? `${r.jobTitle} · ` : ''}{r.clinicName}
                  </div>
                  {r.note && (
                    <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 4, fontStyle: 'italic', overflowWrap: 'anywhere' }}>
                      "{r.note}"
                    </div>
                  )}
                </div>
                {denying !== key && (
                  <>
                    <button className="btn primary" disabled={busyId === key} onClick={() => decide(r, 'approve')}>
                      {busyId === key ? '…' : (isReset ? 'Reset PIN' : 'Approve')}
                    </button>
                    <button className="btn ghost" disabled={busyId === key}
                      onClick={() => (isReset ? decide(r, 'deny') : (setReason(''), setDenying(key)))}>
                      {isReset ? 'Dismiss' : 'Deny'}
                    </button>
                  </>
                )}
              </div>

              {denying === key && (
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                    Deny {r.displayName}'s request? They'll see this and can request again.
                  </div>
                  <textarea className="input" value={reason} onChange={e => setReason(e.target.value)}
                    rows={2} maxLength={500} autoFocus
                    placeholder="Reason (optional) — e.g. incomplete details, CM already in clinic"
                    style={{ width: '100%', resize: 'vertical' }} />
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button className="btn danger filled" disabled={busyId === key} onClick={() => decide(r, 'deny', reason.trim())}>
                      {busyId === key ? '…' : 'Deny request'}
                    </button>
                    <button className="btn ghost" disabled={busyId === key} onClick={() => { setDenying(null); setReason(''); }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          );})}
        </div>
      )}
    </div>
  );
}
